import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Dialog } from '@/components/ui/organisms/Dialog';
import { Button } from '@/components/ui/atoms/Button';
import { getGTMAttributes } from '@/utils/gtm/gtm-tracking';
import type { GTMTrackingAttributes } from '@/utils/gtm/gtm-tracking';
import { useBookingLink } from '@/hooks/useBookingLink';
import {
  acceptCandidatePayload,
  canOfferSaveAsDefault,
  canSendInvitation,
  initialBookingDraft,
  normalizeBookingUrl,
  type AcceptCandidatePayload,
  type BookingLinkDraft,
} from '@/utils/bookingLink';
import { InterviewInvitationFields } from './InterviewInvitationFields';
import { SendNotificationCheckbox } from '../candidate-actions/SendNotificationCheckbox';

export interface InterviewInvitationProps {
  open: boolean;
  onClose: () => void;
  recipientEmail?: string | null;
  candidateName?: string;
  isSubmitting: boolean;
  onSend: (payload: AcceptCandidatePayload) => void;
  closeButtonTracking?: GTMTrackingAttributes;
  sendButtonTracking?: GTMTrackingAttributes;
  /** When false, UI must not promise a recruiter interview invitation. Defaults to true. */
  willNotify?: boolean;
  /** Per-move opt-out. The checkbox renders only when this handler is set. */
  sendNotification?: boolean;
  onSendNotificationChange?: (value: boolean) => void;
}

function InvitationActions({
  sendDisabled,
  isSubmitting,
  onClose,
  onSend,
  closeButtonTracking,
  sendButtonTracking,
  willNotify,
}: {
  sendDisabled: boolean;
  isSubmitting: boolean;
  onClose: () => void;
  onSend: () => void;
  closeButtonTracking?: GTMTrackingAttributes;
  sendButtonTracking?: GTMTrackingAttributes;
  willNotify: boolean;
}) {
  const { t } = useTranslation('candidates');

  return (
    <>
      <Button
        type="outlined"
        intent="secondary"
        onClick={onClose}
        label={t('invitation_popover.close')}
        disabled={isSubmitting}
        {...(closeButtonTracking ? getGTMAttributes(closeButtonTracking) : {})}
      />
      <Button
        type="contained"
        intent="secondary"
        onClick={onSend}
        disabled={sendDisabled}
        loading={isSubmitting}
        label={
          isSubmitting
            ? t('invitation_popover.sending')
            : t(
                willNotify
                  ? 'invitation_popover.send_email'
                  : 'invitation_popover.confirm_without_email'
              )
        }
        {...(sendButtonTracking ? getGTMAttributes(sendButtonTracking) : {})}
      />
    </>
  );
}

export function InterviewInvitation({
  open,
  onClose,
  recipientEmail,
  candidateName,
  isSubmitting,
  onSend,
  closeButtonTracking,
  sendButtonTracking,
  willNotify: stageWillNotify = true,
  sendNotification = true,
  onSendNotificationChange,
}: InterviewInvitationProps) {
  const { t } = useTranslation('candidates');
  const showNotificationToggle = stageWillNotify && Boolean(onSendNotificationChange);
  const willNotify = stageWillNotify && (!showNotificationToggle || sendNotification);
  const { saved, isLoading, saveMutation } = useBookingLink({ enabled: open });
  const [draft, setDraft] = useState<BookingLinkDraft>(() => initialBookingDraft(null));
  const [saveAsDefault, setSaveAsDefault] = useState(false);

  useEffect(() => {
    if (!open) return;
    setDraft(initialBookingDraft(saved));
    setSaveAsDefault(false);
    // Keyed on the saved link's values, not object identity.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, saved?.url, saved?.include_by_default]);

  const title = t('invitation_popover.title');
  const offerSaveAsDefault = canOfferSaveAsDefault(draft, saved);
  // Booking options are hidden when no email goes out, so they must not block the move.
  const sendDisabled = isSubmitting || (willNotify && !canSendInvitation(draft));

  const handleSend = () => {
    if (willNotify && saveAsDefault && offerSaveAsDefault) {
      saveMutation.mutate({ url: normalizeBookingUrl(draft.url) });
    }
    onSend(acceptCandidatePayload(draft, saved));
  };

  const notificationToggle =
    showNotificationToggle && onSendNotificationChange ? (
      <SendNotificationCheckbox
        id="send-acceptance-notification"
        label={t('invitation_popover.send_notification')}
        checked={sendNotification}
        onChange={onSendNotificationChange}
        disabled={isSubmitting}
      />
    ) : undefined;

  const fields = (
    <InterviewInvitationFields
      recipientEmail={recipientEmail}
      candidateName={candidateName}
      draft={draft}
      onDraftChange={setDraft}
      saved={saved}
      isLoading={isLoading}
      saveAsDefault={saveAsDefault}
      onSaveAsDefaultChange={setSaveAsDefault}
      checkboxId="include-booking-link"
      willNotify={willNotify}
      notificationToggle={notificationToggle}
    />
  );
  const actions = (
    <InvitationActions
      sendDisabled={sendDisabled}
      isSubmitting={isSubmitting}
      onClose={onClose}
      onSend={handleSend}
      closeButtonTracking={closeButtonTracking}
      sendButtonTracking={sendButtonTracking}
      willNotify={willNotify}
    />
  );

  return (
    <Dialog
      open={open}
      onOpenChange={(nextOpen) => {
        if (!nextOpen) onClose();
      }}
      title={title}
    >
      <Dialog.Header
        titleVariant="h3"
        title={title}
        showCloseButton
        closeButtonProps={
          closeButtonTracking ? getGTMAttributes(closeButtonTracking) : undefined
        }
      />
      <Dialog.Body className="flex-none">{fields}</Dialog.Body>
      <Dialog.Footer align="right">{actions}</Dialog.Footer>
    </Dialog>
  );
}
